/**
 * useApplicationDivisions — load a competition year's APPLICATION-selectable
 * divisions for the director Form_Builder surfaces.
 *
 * The builder's page/field/document editors take a `DivisionOption[]`
 * (`{ key, label }`) so a director can scope visibility to specific divisions.
 * Those options must be the SAME runtime-configurable divisions the rest of the
 * admin uses, so this hook reads the canonical `listDivisions(competitionYear)`
 * GraphQL query (authMode `userPool`, via `generateClient` from
 * `aws-amplify/api` — mirroring `ContestantNumbering` /
 * `loadDivisionRegistry`), then keeps only the divisions that are both `active`
 * and `appliesToApplications`, sorted by `displayOrder`.
 *
 * It returns both the filtered `Division[]` (for callers that need the full
 * registry shape) and the derived `options` (`{ key, label }`) the builder
 * components consume directly.
 *
 * Validates: application-form-builder director-surface wiring (flag-gated,
 * admin-only). Reuses the division registry as the application-category source.
 */

import { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';

import { listDivisions } from '@/graphql/queries';
import type { DivisionOption } from '@/components/application-form/builder';
// The renderer/pure `Division` re-export is the shared registry shape and
// matches the `listDivisions` query result field-for-field (key, label,
// displayOrder, appliesToApplications, appliesToJudging, active, …).
import type { Division } from '@/components/application-form/renderer/pure';

const client = generateClient();

/** The state returned by {@link useApplicationDivisions}. */
export interface UseApplicationDivisionsResult {
  /** Active, application-selectable divisions for the year, sorted by displayOrder. */
  divisions: Division[];
  /** The `{ key, label }` options the builder components consume. */
  options: DivisionOption[];
  loading: boolean;
  error: string | null;
}

function errorMessage(err: unknown, fallback: string): string {
  const errors = (err as { errors?: Array<{ message?: string }> })?.errors;
  if (errors && errors.length > 0 && errors[0]?.message) return errors[0].message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Load the application-selectable divisions for `competitionYear`.
 *
 * @param competitionYear the operative competition year (from `useActiveSeason()`).
 */
export function useApplicationDivisions(
  competitionYear: number,
): UseApplicationDivisionsResult {
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [options, setOptions] = useState<DivisionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = (await client.graphql({
          query: listDivisions,
          variables: { competitionYear },
          authMode: 'userPool',
        })) as GraphQLResult<{ listDivisions: Division[] }>;
        if (cancelled) return;
        const list = (result.data?.listDivisions ?? [])
          .filter((d) => d.active && d.appliesToApplications)
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setDivisions(list);
        setOptions(list.map((d) => ({ key: d.key, label: d.label })));
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, 'Failed to load divisions.'));
          setDivisions([]);
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionYear]);

  return { divisions, options, loading, error };
}

export default useApplicationDivisions;
